import jsPDF from "jspdf";
import { convertUTCToLocal, formatDob } from "../../../utils/helper";
import { SYMPTOM_STATES, parseSymptomsString, wasCreatedOnOrAfterSymptomsCutoff } from "../../../utils/symptomTriState";
import { layoutBoldAwareLines, drawBoldAwareLine } from "../../../utils/pdfRichText";
import dmSansRegular from "../../case-manager/oor-report/fonts/dmSansRegular";
import dmSansBold from "../../case-manager/oor-report/fonts/dmSansBold";
import dmSansSemiBold from "./fonts/dmSansSemiBold";
import dmSansItalic from "./fonts/dmSansItalic";

// Draws the same layout as FinalizeCareplanPDFView's on-screen preview
// (Figma "CCM Careplan Report PDF"). Every size below is the preview's CSS
// px value: the preview's 714px content column maps onto this PDF's 190mm.
const PX = 190 / 714; // mm per preview px
const pt = (px) => (px * PX) / 0.3528; // font size in pt for a preview px size
const LINE = 1.302; // DM Sans "line-height: normal" (ascent 0.992 + descent 0.310)
const lineMm = (px) => px * PX * LINE;
// Baseline for text whose line box starts at `top` (no half-leading at "normal").
const baselineAt = (top, px) => top + px * PX * 0.992;

const FONT = "DMSans";

const SIZE = {
  title: 17,
  bar: 17,
  subHeading: 14.7,
  value: 12.5,
  label: 10.5,
  meta: 10.5,
  footer: 8.5,
};

// Figma design tokens
const COLORS = {
  primary: [13, 17, 57], // #0D1139
  lightText: [86, 89, 117], // #565975
  line: [201, 203, 213], // #C9CBD5
  secondary: [72, 166, 196], // #48A6C4
  white: [255, 255, 255],
  black: [0, 0, 0],
};

const registerFonts = (pdf) => {
  pdf.addFileToVFS("DMSans-Regular.ttf", dmSansRegular);
  pdf.addFont("DMSans-Regular.ttf", FONT, "normal");
  pdf.addFileToVFS("DMSans-Bold.ttf", dmSansBold);
  pdf.addFont("DMSans-Bold.ttf", FONT, "bold");
  pdf.addFileToVFS("DMSans-SemiBold.ttf", dmSansSemiBold);
  pdf.addFont("DMSans-SemiBold.ttf", FONT, "semibold");
  pdf.addFileToVFS("DMSans-Italic.ttf", dmSansItalic);
  pdf.addFont("DMSans-Italic.ttf", FONT, "italic");
};

// Helper to resize logo image to reduce PDF size
const resizeLogo = (logoSrc, maxWidth = 200, maxHeight = 50) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      // Calculate new dimensions maintaining aspect ratio
      if (width > maxWidth) {
        height = (maxWidth / width) * height;
        width = maxWidth;
      }
      if (height > maxHeight) {
        width = (maxHeight / height) * width;
        height = maxHeight;
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      // Fill with white background first (for transparency)
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);

      // Draw the resized image
      ctx.drawImage(img, 0, 0, width, height);

      // Get data URL as PNG (preserves quality, no artifacts)
      const dataUrl = canvas.toDataURL('image/png');
      resolve({ dataUrl, width, height });
    };
    img.onerror = (e) => {
      console.error("Image load error:", e);
      reject(e);
    };
    img.src = logoSrc;
  });
};

// Same empty-value rules as the preview (SCRUM-743: blank, never "--"/N/A).
const blankIfPlaceholder = (value) => (["--", "None", "N/A"].includes(value) ? "" : value || "");
const convertDate = (date) => blankIfPlaceholder(convertUTCToLocal(date));
const formatDateOfBirth = (date) => blankIfPlaceholder(formatDob(date));

const formatStatus = (status) => {
  if (["--", "None", "N/A"].includes(status)) return "";
  const statusMap = {
    finalized: "Finalized",
    in_progress: "In Progress",
    completed: "Completed"
  };
  return statusMap[status] || status || "";
};

// Free-text fields (Prognosis, Treatment Goals, ...) split into their
// non-blank lines so each line becomes one numbered item, as in the preview.
const textLines = (text) =>
  (text || "").split("\n").map((line) => line.trim()).filter(Boolean);

const toStrings = (items) => (items || []).map((item) => (item ?? "").toString());

const cleanListItems = (items) =>
  (Array.isArray(items) ? items : [])
    .map((item) => (item || "").toString().replace(/\s*=\s*undefined\s*$/i, "").trim())
    .filter((item) => item);

const formatMedicationLines = (meds) => {
  const items = Array.isArray(meds) ? meds : (meds ? [meds] : []);
  return items
    .flatMap((m) => (m?.name || "").split(/\r?\n/))
    .map((l) => (l || "").replace(/\s*=\s*undefined\s*$/i, "").trim())
    .filter((l) => l && !["--", "None", "N/A"].includes(l));
};

// Symptoms: a reported (YES) symptom reads "Reports <name>" in bold and a
// negative one "Denied <name>", in place of the on-screen check/X icons. A
// qualified symptom (SCRUM-788, e.g. "headache — not reported") already
// states its state in words, so it is shown as-is.
const formatSymptomItems = (text) =>
  parseSymptomsString(text || "")
    .filter((symptom) => symptom.name.trim())
    .map((symptom) => {
      if (symptom.qualifier) return `${symptom.name} — ${symptom.qualifier}`;
      if (symptom.state !== SYMPTOM_STATES.YES) return `Denied ${symptom.name}`;
      // Nested "**" can't be bolded again, so AI-bolded names keep their own markers.
      return symptom.name.includes("**") ? `**Reports** ${symptom.name}` : `**Reports ${symptom.name}**`;
    });

const generateFinalizeCareplanPDF = async ({
  patient,
  carePlanData,
  conditions,
  medications,
  reportDate,
  logo,
}) => {
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });
  registerFonts(pdf);

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 10; // left/right margin
  const topMargin = 8;
  const contentWidth = pageWidth - margin * 2;
  const footerLineY = pageHeight - 14;
  const contentBottom = footerLineY - 20 * PX; // preview footer margin-top: 20px
  let y = topMargin;

  const patientName = `${patient?.user?.first_name || ""} ${patient?.user?.last_name || ""}`;
  const nurseName = (patient?.nurse?.first_name && patient?.nurse?.last_name)
    ? `${patient.nurse.first_name} ${patient.nurse.last_name}`
    : (patient?.nurse?.first_name || patient?.nurse?.last_name || "");
  const finalizedBy = carePlanData?.finalized_by_name || carePlanData?.finalized_by || "";
  // Backend stamps assigned_at when the care plan is completed, and keeps it
  // once the plan is finalized.
  const completedAt = carePlanData?.assigned_at;
  const generationTime = convertUTCToLocal(new Date().toISOString(), navigator.language, true);

  // Filter conditions to show only those marked for billing
  const billedConditions = (conditions || []).filter((cond) => cond?.for_billing_icd === true);

  const getBehavioralVal = (key1, key2) =>
    blankIfPlaceholder(carePlanData?.behavioral_data?.[key1] || carePlanData?.behavioral_data?.[key2] || "");

  // ---- drawing primitives -------------------------------------------------

  const setText = (px, style, color) => {
    pdf.setFont(FONT, style);
    pdf.setFontSize(pt(px));
    pdf.setTextColor(...color);
  };

  const hLine = (x1, x2, lineY, color = COLORS.line) => {
    pdf.setDrawColor(...color);
    pdf.setLineWidth(PX);
    pdf.line(x1, lineY, x2, lineY);
  };

  const vLine = (x, y1, y2) => {
    pdf.setDrawColor(...COLORS.line);
    pdf.setLineWidth(PX);
    pdf.line(x, y1, x, y2);
  };

  // Moves to a new page when `height` more mm won't fit above the footer.
  const ensureSpace = (height) => {
    if (y + height > contentBottom) {
      pdf.addPage();
      y = topMargin;
      return true;
    }
    return false;
  };

  const richOptions = (px, maxWidth) => ({
    maxWidth,
    fontName: FONT,
    fontSize: pt(px),
    lineHeight: lineMm(px),
    normalColor: COLORS.primary,
    boldColor: COLORS.black,
    dateTagFontSize: pt(9),
  });

  const layoutRich = (text, px, maxWidth) => {
    const lines = layoutBoldAwareLines(pdf, text || "", richOptions(px, maxWidth));
    return { lines, height: Math.max(lines.length, 1) * lineMm(px) };
  };

  const drawRich = (layout, x, top, px, maxWidth) => {
    layout.lines.forEach((lineTokens, i) => {
      drawBoldAwareLine(pdf, lineTokens, x, baselineAt(top + i * lineMm(px), px), richOptions(px, maxWidth));
    });
  };

  // Mixed regular/semibold runs on one line, e.g. the footer's
  // "Nurse **name** | Reviewed by **name**".
  const drawRuns = (runs, x, baseline, px, align = "left") => {
    const widths = runs.map(([text, style]) => {
      pdf.setFont(FONT, style);
      pdf.setFontSize(pt(px));
      return pdf.getTextWidth(text);
    });
    let cursorX = align === "right" ? x - widths.reduce((a, b) => a + b, 0) : x;
    runs.forEach(([text, style], i) => {
      setText(px, style, COLORS.lightText);
      pdf.text(text, cursorX, baseline);
      cursorX += widths[i];
    });
  };

  // ---- layout blocks (each mirrors a preview CSS block) -------------------

  // .header
  const drawHeader = async () => {
    const rightX = pageWidth - margin;
    const logoWidth = 145 * PX;
    let logoHeight = logoWidth * (214 / 1166);

    if (logo) {
      try {
        const resized = await resizeLogo(logo, 600, 120);
        logoHeight = logoWidth * (resized.height / resized.width);
        pdf.addImage(resized.dataUrl, "PNG", margin, topMargin, logoWidth, logoHeight);
      } catch (e) {
        console.warn("Could not add logo image:", e);
      }
    }

    // Navy rule under the logo, then the location name
    let leftY = topMargin + logoHeight + 8 * PX;
    pdf.setFillColor(...COLORS.primary);
    pdf.rect(margin, leftY, logoWidth, 2 * PX, "F");
    leftY += 2 * PX + 8 * PX;
    setText(SIZE.value, "semibold", COLORS.lightText);
    pdf.text(patient?.location?.name || "", margin, baselineAt(leftY, SIZE.value));
    const leftBottom = leftY + lineMm(SIZE.value);

    // Right column is bottom-aligned with the left one
    const completedTop = leftBottom - lineMm(SIZE.meta);
    const monthTop = completedTop - 5 * PX - lineMm(SIZE.value);
    setText(SIZE.value, "semibold", COLORS.lightText);
    pdf.text(reportDate || "", rightX, baselineAt(monthTop, SIZE.value), { align: "right" });
    setText(SIZE.meta, "italic", COLORS.lightText);
    pdf.text(`Completed date: ${convertDate(completedAt)}`, rightX, baselineAt(completedTop, SIZE.meta), { align: "right" });

    // Title row
    const rowTop = leftBottom + 5 * PX;
    const titleHeight = lineMm(SIZE.title);
    setText(SIZE.title, "semibold", COLORS.primary);
    pdf.text("CCM Careplan", margin, baselineAt(rowTop, SIZE.title));
    setText(SIZE.meta, "italic", COLORS.lightText);
    pdf.text(
      `Finalized date: ${convertDate(carePlanData?.finalized_at)}`,
      rightX,
      baselineAt(rowTop + (titleHeight - lineMm(SIZE.meta)) / 2, SIZE.meta),
      { align: "right" }
    );

    y = rowTop + titleHeight + 5 * PX;
    hLine(margin, rightX, y);
    y += 10 * PX;
  };

  // .infoSectionHeader - kept on the same page as the start of its content
  const drawSectionBar = (title, strongText = "") => {
    const barHeight = 27 * PX;
    ensureSpace(barHeight + 25);
    pdf.setFillColor(...COLORS.primary);
    pdf.rect(margin, y, contentWidth, barHeight, "F");
    const textX = margin + 10.5 * PX;
    const baseline = baselineAt(y + (barHeight - lineMm(SIZE.bar)) / 2, SIZE.bar);
    setText(SIZE.bar, "normal", COLORS.white);
    pdf.text(title, textX, baseline);
    if (strongText) {
      const titleWidth = pdf.getTextWidth(title);
      setText(SIZE.bar, "semibold", COLORS.white);
      pdf.text(strongText, textX + titleWidth, baseline);
    }
    y += barHeight;
  };

  // .infoSection margin-bottom
  const endSection = () => {
    y += 16 * PX;
  };

  // .subSectionHeading - `keepWith` is the height of the block that must
  // follow it on the same page, so a heading is never stranded at a page end.
  const drawSubHeading = (title, keepWith = 12) => {
    ensureSpace(10.5 * PX + lineMm(SIZE.subHeading) + 5 * PX + keepWith);
    y += 10.5 * PX;
    setText(SIZE.subHeading, "semibold", COLORS.secondary);
    pdf.text(title, margin + 5 * PX, baselineAt(y, SIZE.subHeading));
    y += lineMm(SIZE.subHeading) + 5 * PX;
    hLine(margin + 5 * PX, pageWidth - margin - 5 * PX, y);
  };

  // .infoGrid: three columns of label/value fields with grey dividers
  const INFO_GRID_PAD_LEFT = 10.5 * PX;
  const infoColumnWidth = (contentWidth - INFO_GRID_PAD_LEFT - 5 * PX) / 3;
  const infoColumnInset = (i) => (i === 0 ? 0 : 10.5 * PX);

  const layoutInfoGrid = (columns) => {
    const columnWidth = infoColumnWidth;
    const columnInset = infoColumnInset;
    const laidOut = columns.map((fields, i) => {
      const textWidth = columnWidth - columnInset(i) - 1.5;
      let height = 0;
      const items = fields.map(([label, value], j) => {
        const valueLayout = layoutRich(value, SIZE.value, textWidth);
        const fieldHeight = lineMm(SIZE.label) + 4 * PX + valueLayout.height;
        const item = { label, valueLayout, offset: height + (j > 0 ? 10.5 * PX : 0) };
        height = item.offset + fieldHeight;
        return item;
      });
      return { items, height, textWidth };
    });
    return { laidOut, height: 10.5 * PX + Math.max(...laidOut.map((column) => column.height)) };
  };

  const drawInfoGrid = (columns) => {
    const { laidOut, height } = layoutInfoGrid(columns);
    const gridHeight = height - 10.5 * PX;

    ensureSpace(height);
    y += 10.5 * PX;
    laidOut.forEach((column, i) => {
      const columnX = margin + INFO_GRID_PAD_LEFT + infoColumnWidth * i;
      if (i > 0) vLine(columnX, y, y + gridHeight);
      const textX = columnX + infoColumnInset(i);
      column.items.forEach((item) => {
        const fieldTop = y + item.offset;
        setText(SIZE.label, "semibold", COLORS.lightText);
        pdf.text(item.label, textX, baselineAt(fieldTop, SIZE.label));
        const valueTop = fieldTop + lineMm(SIZE.label) + 4 * PX;
        drawRich(item.valueLayout, textX, valueTop, SIZE.value, column.textWidth);
      });
    });
    y += gridHeight;
  };

  // A numbered list cell ("1. text"), shared by the boxed and unboxed lists.
  // padTop/padRight/padBottom/padLeft are the preview's item paddings.
  const layoutNumberedCell = (item, index, cellWidth, pad) => {
    setText(SIZE.value, "normal", COLORS.primary);
    const indexText = `${index + 1}.`;
    const indexWidth = pdf.getTextWidth(indexText) + 5 * PX;
    const textWidth = cellWidth - pad.left - pad.right - indexWidth;
    const layout = layoutRich(item.text, SIZE.value, textWidth);
    return { item, indexText, indexWidth, textWidth, layout, height: layout.height + pad.top + pad.bottom };
  };

  const drawNumberedCell = (cell, x, top, rowHeight, pad, centered) => {
    const textTop = centered ? top + (rowHeight - cell.layout.height) / 2 : top + pad.top;
    let textX = x + pad.left;
    setText(SIZE.value, "normal", COLORS.primary);
    pdf.text(cell.indexText, textX, baselineAt(textTop, SIZE.value));
    textX += cell.indexWidth;
    drawRich(cell.layout, textX, textTop, SIZE.value, cell.textWidth);
  };

  const toItems = (items) => items.map((item) => (typeof item === "string" ? { text: item } : item));

  // .listBox: bordered box, blue title row, numbered items row-major across
  // `columns`. Rows that don't fit continue on the next page.
  const LIST_BOX_PAD = { top: 3 * PX, right: 10.5 * PX, bottom: 3 * PX, left: 19 * PX };
  const listBoxTitleHeight = 24 * PX;

  const layoutListBox = (items, columns, width, emptyText) => {
    const cellWidth = width / columns;
    const cells = toItems(items).map((item, i) => layoutNumberedCell(item, i, cellWidth, LIST_BOX_PAD));
    const rows = [];
    for (let i = 0; i < cells.length; i += columns) {
      const rowCells = cells.slice(i, i + columns);
      rows.push({ cells: rowCells, height: Math.max(24 * PX, ...rowCells.map((cell) => cell.height)) });
    }
    if (rows.length === 0) rows.push({ cells: [], emptyText, height: 24 * PX });
    const height = listBoxTitleHeight + rows.reduce((sum, row) => sum + row.height, 0);
    return { rows, cellWidth, height };
  };

  const drawListBox = (title, items, {
    columns = 2,
    x = margin + 5 * PX,
    width = contentWidth - 10 * PX,
    emptyText = "",
    allowPageBreak = true,
  } = {}) => {
    const box = layoutListBox(items, columns, width, emptyText);
    if (allowPageBreak) ensureSpace(listBoxTitleHeight + box.rows[0].height);

    let segmentTop = y;
    const closeSegment = () => {
      pdf.setDrawColor(...COLORS.line);
      pdf.setLineWidth(PX);
      pdf.rect(x, segmentTop, width, y - segmentTop);
    };

    setText(SIZE.subHeading, "semibold", COLORS.secondary);
    pdf.text(title, x + 5 * PX, baselineAt(y + (listBoxTitleHeight - lineMm(SIZE.subHeading)) / 2, SIZE.subHeading));
    y += listBoxTitleHeight;
    hLine(x, x + width, y);

    box.rows.forEach((row) => {
      if (allowPageBreak && y + row.height > contentBottom) {
        closeSegment();
        pdf.addPage();
        y = topMargin;
        segmentTop = y;
      }
      if (row.emptyText) {
        setText(SIZE.value, "italic", COLORS.black);
        pdf.text(row.emptyText, x + LIST_BOX_PAD.left, baselineAt(y + (row.height - lineMm(SIZE.value)) / 2, SIZE.value));
      }
      // Dividers on every row, even when the last row is short
      if (!row.emptyText) {
        for (let i = 1; i < columns; i++) vLine(x + box.cellWidth * i, y, y + row.height);
      }
      row.cells.forEach((cell, i) => {
        drawNumberedCell(cell, x + box.cellWidth * i, y, row.height, LIST_BOX_PAD, true);
      });
      y += row.height;
    });
    closeSegment();
  };

  // .listBoxGroup spacing
  const startListBoxGroup = () => {
    y += 10.5 * PX;
  };
  const listBoxGap = () => {
    y += 10.5 * PX;
  };

  // Two boxes side by side (Allergies / Side Effects), kept on one page.
  const drawListBoxPair = (left, right) => {
    const gap = 10.5 * PX;
    const width = (contentWidth - 10 * PX - gap) / 2;
    const leftX = margin + 5 * PX;
    const rightX = leftX + width + gap;
    const height = Math.max(
      layoutListBox(left.items, 1, width, "").height,
      layoutListBox(right.items, 1, width, "").height
    );
    ensureSpace(height);
    const startY = y;
    drawListBox(left.title, left.items, { columns: 1, x: leftX, width, allowPageBreak: false });
    const leftEnd = y;
    y = startY;
    drawListBox(right.title, right.items, { columns: 1, x: rightX, width, allowPageBreak: false });
    y = Math.max(leftEnd, y);
  };

  // .numberedGrid / .numberedLines: unboxed numbered list, row-major across
  // `columns` with grey dividers between columns.
  const NUMBERED_PAD = { top: 5 * PX, right: 10.5 * PX, bottom: 5 * PX, left: 10.5 * PX };

  const drawNumberedGrid = (items, columns = 3) => {
    if (items.length === 0) return;
    const x = margin + 5 * PX;
    const cellWidth = (contentWidth - 10 * PX) / columns;
    const cells = toItems(items).map((item, i) => layoutNumberedCell(item, i, cellWidth, NUMBERED_PAD));
    y += 5 * PX;
    const rows = [];
    for (let i = 0; i < cells.length; i += columns) rows.push(cells.slice(i, i + columns));
    rows.forEach((rowCells) => {
      const rowHeight = Math.max(...rowCells.map((cell) => cell.height));
      ensureSpace(rowHeight);
      const rowTop = y;
      // Dividers on every row, even when the last row is short
      for (let j = 1; j < columns; j++) vLine(x + cellWidth * j, rowTop, rowTop + rowHeight);
      rowCells.forEach((cell, j) => {
        drawNumberedCell(cell, x + cellWidth * j, rowTop, rowHeight, NUMBERED_PAD, false);
      });
      y += rowHeight;
    });
  };

  // .notesText: paragraph text that may run across pages, line by line.
  const drawParagraph = (text, { italic = false } = {}) => {
    const x = margin + 5 * PX;
    const width = contentWidth - 10 * PX;
    y += 10.5 * PX;
    if (italic) {
      ensureSpace(lineMm(SIZE.value));
      setText(SIZE.value, "italic", COLORS.black);
      pdf.text(text, x, baselineAt(y, SIZE.value));
      y += lineMm(SIZE.value);
      return;
    }
    layoutRich(text, SIZE.value, width).lines.forEach((lineTokens) => {
      ensureSpace(lineMm(SIZE.value));
      drawBoldAwareLine(pdf, lineTokens, x, baselineAt(y, SIZE.value), richOptions(SIZE.value, width));
      y += lineMm(SIZE.value);
    });
  };

  // .pageFooter, drawn on every page once the page count is known
  const drawFooter = (pageNumber, totalPages) => {
    const rightX = pageWidth - margin;
    hLine(margin, rightX, footerLineY);
    const firstTop = footerLineY + 10.5 * PX;
    const secondTop = firstTop + lineMm(SIZE.footer) + 5 * PX;
    const first = baselineAt(firstTop, SIZE.footer);
    const second = baselineAt(secondTop, SIZE.footer);

    drawRuns([
      ["Nurse ", "normal"], [nurseName, "semibold"],
      ["  |  Reviewed by ", "normal"], [finalizedBy, "semibold"],
      ["  |  Finalised ", "normal"], [convertDate(carePlanData?.finalized_at), "semibold"],
    ], margin, first, SIZE.footer);
    drawRuns([[`${patientName}  |  ${patient?.location?.name || ""}  |  CCM Careplan`, "normal"]], margin, second, SIZE.footer);
    drawRuns([["Downloaded at ", "normal"], [generationTime, "semibold"]], rightX, first, SIZE.footer, "right");
    drawRuns([[`${pageNumber} of ${totalPages}`, "normal"]], rightX, second, SIZE.footer, "right");
  };

  // ---- document -----------------------------------------------------------

  await drawHeader();

  // Patient Information
  drawSectionBar("Patient Information");
  drawInfoGrid([
    [
      ["Patient Name :", patientName],
      ["Case Manager :", patient?.case_manager?.first_name || patient?.case_manager?.last_name
        ? `${patient.case_manager.first_name || ""} ${patient.case_manager.last_name || ""}`.trim()
        : ""],
      ["Referring Physician :", patient?.provider
        ? `${patient?.provider?.first_name || ""} ${patient?.provider?.last_name || ""}`.trim() || ""
        : ""],
    ],
    [
      ["DOB :", formatDateOfBirth(patient?.user?.dob)],
      ["Status :", formatStatus(carePlanData?.status)],
    ],
    [
      ["Phone :", patient?.user?.primary_phone || ""],
      ["Nurse :", nurseName],
    ],
  ]);
  endSection();

  // Careplan Details
  drawSectionBar("Careplan Details");
  drawInfoGrid([
    [
      ["Created At :", convertDate(carePlanData?.created_at)],
      ["Finalized By :", finalizedBy],
    ],
    [
      ["Created By :", carePlanData?.created_by_name || carePlanData?.created_by || ""],
      ["Last Updated At :", convertDate(carePlanData?.updated_at)],
    ],
    [
      ["Finalized At :", convertDate(carePlanData?.finalized_at)],
      ["Complex :", carePlanData?.complex ? "Yes" : "No"],
    ],
  ]);
  startListBoxGroup();
  drawListBox(
    "Chronic Condition",
    billedConditions.map((cond) => `${cond?.name || "Unknown"}-(${cond?.icd_code || "N/A"})`)
  );
  endSection();

  // Medication
  drawSectionBar("Medication");
  startListBoxGroup();
  drawListBox("Medicines", formatMedicationLines(medications), { emptyText: "No medications recorded." });
  listBoxGap();
  drawListBoxPair(
    { title: "Allergies", items: cleanListItems(carePlanData?.allergies) },
    { title: "Side Effects", items: cleanListItems(carePlanData?.reported_side_effect) }
  );
  endSection();

  // Health Summary
  drawSectionBar("Health Summary");
  const physiologicalColumns = [
    [
      ["Blood Pressure :", carePlanData?.physiological_data?.blood_pressure || ""],
      ["Blood Glucose :", carePlanData?.physiological_data?.blood_glucose || ""],
    ],
    [
      ["Height :", carePlanData?.physiological_data?.height || ""],
      ["BMI :", carePlanData?.physiological_data?.bmi || ""],
    ],
    [
      ["Weight :", carePlanData?.physiological_data?.weight || ""],
    ],
  ];
  drawSubHeading("Physiological Averages", layoutInfoGrid(physiologicalColumns).height);
  drawInfoGrid(physiologicalColumns);
  // Sleep/Exercise/Diet-Water carry AI "**bold**(added MM/DD/YYYY)" markers,
  // rendered by the rich text helper like every other AI-populated field.
  const behavioralColumns = [
    [["Sleep :", getBehavioralVal('sleep_hours', 'sleep')]],
    [["Exercise :", getBehavioralVal('exercise_hours_per_week', 'exercise')]],
    [["Diet / Water :", getBehavioralVal('water_intake', 'water_diet')]],
  ];
  drawSubHeading("Behavioral Averages", layoutInfoGrid(behavioralColumns).height);
  drawInfoGrid(behavioralColumns);
  endSection();

  // Problem List & Education
  drawSectionBar("Problem List & Education");
  drawSubHeading("PMHx");
  drawNumberedGrid(toStrings(carePlanData?.pmhx));
  drawSubHeading("Potential Barriers");
  drawNumberedGrid(toStrings(carePlanData?.potential_barriers));
  drawSubHeading("Recent Hospitalization");
  drawNumberedGrid(toStrings(carePlanData?.recent_hospitalization));
  startListBoxGroup();
  drawListBox("Recommendations", toStrings(carePlanData?.recommendations));
  endSection();

  // Functional Assessment
  drawSectionBar("Functional Assessment");
  startListBoxGroup();
  drawListBox("ADLS", toStrings(carePlanData?.activities_of_daily_living));
  listBoxGap();
  drawListBox("Caregiver Assessment", toStrings(carePlanData?.caregiver_assessment));
  endSection();

  // Chronic Conditions - one section per billed condition
  if (billedConditions.length > 0) {
    billedConditions.forEach((cond, index) => {
      drawSectionBar(
        `Chronic Conditions #${index + 1} - `,
        `${cond?.name || ""}${cond?.icd_code ? ` - (${cond.icd_code})` : ""}`
      );
      drawSubHeading("Prognosis");
      drawNumberedGrid(textLines(cond.prognosis), 1);
      startListBoxGroup();
      drawListBox(
        "Symptoms",
        wasCreatedOnOrAfterSymptomsCutoff(carePlanData?.created_at)
          ? formatSymptomItems(cond.symptoms)
          : textLines(cond.symptoms)
      );
      listBoxGap();
      drawListBox("Treatment Goals", textLines(cond.treatment_goals));
      listBoxGap();
      drawListBox(
        "Planned Interventions",
        textLines(cond.planned_interventions).map((line) => {
          const parts = line.split(":::");
          return parts.length > 1 ? `${parts[0]}: ${parts[1]}` : line;
        })
      );
      endSection();
    });
  } else {
    drawSectionBar("Chronic Conditions");
    drawParagraph("No chronic conditions recorded.", { italic: true });
    endSection();
  }

  // Careplan Notes
  drawSectionBar("Careplan Notes");
  if (carePlanData?.note) {
    drawParagraph(carePlanData.note);
  } else {
    drawParagraph("No notes available.");
  }

  const totalPages = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    drawFooter(i, totalPages);
  }

  return pdf;
};

export default generateFinalizeCareplanPDF;
